/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type { ReactNode } from 'react'

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { cn } from '@/lib/utils'

export interface FaqListItem {
  question: string
  answer: string
  icon?: ReactNode
}

export interface FaqListProps {
  items: FaqListItem[]
  /** Visible heading; defaults to Frequently Asked Questions */
  heading?: string
  headingClassName?: string
  itemClassName?: string
  itemHoverClassName?: string
  questionClassName?: string
  answerClassName?: string
  iconClassName?: string
}

const DEFAULT_HEADING = 'Frequently Asked Questions'

export function FaqList({
  items,
  heading = DEFAULT_HEADING,
  headingClassName,
  itemClassName,
  itemHoverClassName,
  questionClassName,
  answerClassName,
  iconClassName,
}: FaqListProps) {
  return (
    <div className="flex w-full flex-col gap-10">
      <h2 className={cn('text-center font-extrabold text-4xl text-foreground leading-none', headingClassName)}>
        {heading}
      </h2>

      <Accordion className="w-full" defaultValue={items[0] ? ['faq-0'] : undefined} multiple>
        {items.map((item, index) => (
          <AccordionItem
            className={cn(
              'mb-5 rounded-xl border border-border/60 not-last:border-b-0 bg-card/50 transition-colors last:mb-0 hover:border-border',
              itemHoverClassName,
              itemClassName
            )}
            key={item.question}
            value={`faq-${index}`}
          >
            <AccordionTrigger
              className={cn(
                'cursor-pointer border-0 bg-transparent p-6 text-left hover:bg-transparent hover:no-underline focus-visible:no-underline',
                questionClassName
              )}
            >
              <span className="flex w-full items-center gap-3 text-left">
                {item.icon ? (
                  <span aria-hidden className={cn('inline-flex text-muted-foreground', iconClassName)}>
                    {item.icon}
                  </span>
                ) : null}
                <span className="flex-1 font-bold text-foreground text-lg">{item.question}</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className={cn('px-6 pt-0 pb-6 text-muted-foreground', answerClassName)}>
              {item.answer}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  )
}
